<template>
  <AdminLayout page-title="Admin Dashboard" page-subtitle="Manage your OilBuddy platform">
    <!-- Dashboard Content -->
    <div class="space-y-8">

      <!-- Platform Stats Overview -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <!-- Total Users Card -->
        <div class="admin-stat-card group">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="text-sm font-medium text-slate-400 mb-2">Total Users</p>
              <p class="text-3xl font-bold text-white">{{ Number(adminStats.totalUsers || 0).toLocaleString() }}</p>
              <p class="text-sm text-emerald-400 mt-3">
                <span class="inline-flex items-center">
                  <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
            </svg>
                  +{{ adminStats.newUsersThisMonth }} this month
                </span>
              </p>
        </div>
            <div class="w-12 h-12 bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-blue-500/30 rounded-2xl flex items-center justify-center">
              <svg class="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a4 4 0 11-8 0 4 4 0 018 0z"/>
              </svg>
            </div>
          </div>
        </div>

        <!-- Active Jobs Card -->
        <div class="admin-stat-card group">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="text-sm font-medium text-slate-400 mb-2">Active Jobs</p>
              <p class="text-3xl font-bold text-white">{{ adminStats.activeJobs }}</p>
              <p class="text-sm text-emerald-400 mt-3">
                <span class="inline-flex items-center">
                  <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                  </svg>
                  {{ adminStats.jobCompletionRate }}% completion rate
                </span>
              </p>
            </div>
            <div class="w-12 h-12 bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30 rounded-2xl flex items-center justify-center">
              <svg class="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6"/>
              </svg>
            </div>
          </div>
        </div>

        <!-- Revenue Card -->
        <div class="admin-stat-card group">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="text-sm font-medium text-slate-400 mb-2">Revenue</p>
              <p class="text-3xl font-bold text-white">${{ (adminStats.totalRevenue / 1000).toFixed(0) }}K</p>
              <p class="text-sm text-emerald-400 mt-3">
                <span class="inline-flex items-center">
                  <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                  </svg>
                  +{{ adminStats.revenueGrowth }}% growth
                </span>
              </p>
            </div>
            <div class="w-12 h-12 bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30 rounded-2xl flex items-center justify-center">
              <svg class="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
          </div>
        </div>

        <!-- Platform Health Card -->
        <div class="admin-stat-card group">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="text-sm font-medium text-slate-400 mb-2">Platform Health</p>
              <p class="text-3xl font-bold text-white">{{ adminStats.platformHealth }}%</p>
              <p class="text-sm text-emerald-400 mt-3">
                <span class="inline-flex items-center">
                  <div class="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></div>
                  All systems operational
                </span>
              </p>
            </div>
            <div class="w-12 h-12 bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-2xl flex items-center justify-center">
              <svg class="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
              </svg>
            </div>
            </div>
          </div>
        </div>

      <!-- Quick Actions Section -->
      <div class="admin-card">
        <div class="admin-card-header">
          <h3 class="text-xl font-bold text-white">Quick Actions</h3>
          <p class="text-slate-400">Manage your platform efficiently</p>
          </div>
        <div class="admin-card-body">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <!-- Add New User -->
            <button @click="showUserModal = true" class="admin-action-card group">
              <div class="w-16 h-16 bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-blue-500/30 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                <svg class="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                  </svg>
                </div>
              <h4 class="font-bold text-white mb-2">Add New User</h4>
              <p class="text-sm text-slate-400">Create contractor or company account</p>
              </button>

            <!-- Create Job -->
            <button @click="showJobModal = true" class="admin-action-card group">
              <div class="w-16 h-16 bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                <svg class="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6"/>
                  </svg>
                </div>
              <h4 class="font-bold text-white mb-2">Create Job</h4>
              <p class="text-sm text-slate-400">Post new oilfield job listing</p>
              </button>

            <!-- View Analytics -->
            <button @click="viewAnalytics" class="admin-action-card group">
              <div class="w-16 h-16 bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                <svg class="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                  </svg>
                </div>
              <h4 class="font-bold text-white mb-2">View Analytics</h4>
              <p class="text-sm text-slate-400">Platform performance insights</p>
              </button>

            <!-- Manage Plans -->
            <button @click="managePlans" class="admin-action-card group">
              <div class="w-16 h-16 bg-gradient-to-br from-orange-500/20 to-red-500/20 border border-orange-500/30 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                <svg class="w-8 h-8 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                </svg>
              </div>
              <h4 class="font-bold text-white mb-2">Manage Plans</h4>
              <p class="text-sm text-slate-400">Configure pricing plans</p>
            </button>

            <!-- Manage Blog -->
            <button @click="manageBlog" class="admin-action-card group">
              <div class="w-16 h-16 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                <svg class="w-8 h-8 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"/>
                </svg>
              </div>
              <h4 class="font-bold text-white mb-2">Manage Blog</h4>
              <p class="text-sm text-slate-400">Create and publish posts</p>
            </button>
            </div>
          </div>
        </div>

      <!-- Recent Activity & Safety Alerts Row -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <!-- Recent Activity -->
        <div class="admin-card">
          <div class="admin-card-header">
            <h3 class="text-xl font-bold text-white">Recent Activity</h3>
            <button @click="refreshData" class="admin-btn-outline">
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
              </svg>
              Refresh
            </button>
          </div>
          <div class="admin-card-body">
            <div class="space-y-4" v-if="recentActivity.length > 0">
              <div v-for="activity in recentActivity" :key="activity.id" class="activity-item">
                <div :class="getActivityIconClass(activity.type)" class="activity-icon">
                  <svg class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                  </svg>
                </div>
                <div class="flex-1">
                  <p class="text-sm font-medium text-white">{{ activity.description }}</p>
                  <p class="text-xs text-slate-400">{{ activity.timestamp }}</p>
                </div>
                <span :class="getActivityStatusBadgeClass(activity.status)" class="activity-badge">
                  {{ activity.status }}
                </span>
                <button @click="deleteActivity(activity.id)" class="text-red-400 hover:text-red-300 ml-2">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1H8a1 1 0 00-1 1v3M4 7h16"/>
                  </svg>
                </button>
              </div>
            </div>
            <div v-else class="text-center py-12">
              <svg class="w-16 h-16 text-slate-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
              </svg>
              <p class="text-slate-400">No recent activity</p>
          </div>
        </div>
      </div>

        <!-- Safety Alerts -->
        <div class="admin-card">
          <div class="admin-card-header">
            <h3 class="text-xl font-bold text-white">Safety Alerts</h3>
            <span v-if="safetyAlerts.length > 0" class="admin-badge-danger">{{ safetyAlerts.length }} Active</span>
            <span v-else class="admin-badge-success">No Alerts</span>
          </div>
          <div class="admin-card-body">
            <div class="space-y-4" v-if="safetyAlerts.length > 0">
              <div v-for="alert in safetyAlerts" :key="alert.id" class="safety-alert-item">
                <div :class="getSafetyAlertIconClass(alert.severity)" class="w-10 h-10 rounded-xl flex items-center justify-center">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path v-if="alert.severity === 'critical'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                    <path v-else-if="alert.severity === 'warning'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
                <div class="flex-1">
                  <p class="text-sm font-medium text-white">{{ alert.title }}</p>
                  <p class="text-xs text-slate-400">{{ alert.location }} • {{ alert.timestamp }}</p>
                </div>
                <div class="flex items-center space-x-2">
                  <button @click="reviewAlert(alert.id)" class="admin-btn-outline text-xs">Review</button>
                  <button @click="deleteAlert(alert.id)" class="text-red-400 hover:text-red-300">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1H8a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            <div v-else class="text-center py-12">
              <svg class="w-16 h-16 text-slate-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                </svg>
              <p class="text-slate-400">No safety alerts</p>
            </div>
          </div>
        </div>
      </div>

      <!-- System Status -->
      <div class="admin-card">
        <div class="admin-card-header">
          <h3 class="text-xl font-bold text-white">System Status</h3>
          <div class="flex items-center space-x-2">
            <div class="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
            <span class="text-sm text-emerald-400">All Systems Operational</span>
          </div>
                  </div>
        <div class="admin-card-body">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div class="system-status-item">
              <div class="flex items-center justify-between mb-2">
                <span class="text-sm font-medium text-slate-300">API Response Time</span>
                <span class="text-sm text-emerald-400">125ms</span>
                  </div>
              <div class="w-full bg-slate-700 rounded-full h-2">
                <div class="bg-emerald-400 h-2 rounded-full" style="width: 85%"></div>
                  </div>
                </div>

            <div class="system-status-item">
              <div class="flex items-center justify-between mb-2">
                <span class="text-sm font-medium text-slate-300">Database Performance</span>
                <span class="text-sm text-emerald-400">98.7%</span>
              </div>
              <div class="w-full bg-slate-700 rounded-full h-2">
                <div class="bg-emerald-400 h-2 rounded-full" style="width: 99%"></div>
                  </div>
                </div>

            <div class="system-status-item">
              <div class="flex items-center justify-between mb-2">
                <span class="text-sm font-medium text-slate-300">Server Uptime</span>
                <span class="text-sm text-emerald-400">99.9%</span>
              </div>
              <div class="w-full bg-slate-700 rounded-full h-2">
                <div class="bg-emerald-400 h-2 rounded-full" style="width: 100%"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add User Modal -->
    <div v-if="showUserModal" class="fixed inset-0 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div class="bg-slate-800/90 backdrop-blur-xl border border-slate-700/50 rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div class="admin-card-header">
          <h3 class="text-xl font-bold text-white">Add New User</h3>
          <button @click="showUserModal = false" class="text-slate-400 hover:text-emerald-400 transition-colors">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div class="p-6">
          <form @submit.prevent="createUser" class="space-y-6">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">First Name</label>
                <input v-model="userForm.firstName" type="text" required class="admin-input" placeholder="Enter first name">
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Last Name</label>
                <input v-model="userForm.lastName" type="text" required class="admin-input" placeholder="Enter last name">
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Email</label>
              <input v-model="userForm.email" type="email" required class="admin-input" placeholder="Enter email address">
              </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">User Type</label>
              <select v-model="userForm.userType" required class="admin-select">
                  <option value="">Select user type</option>
                  <option value="contractor">Contractor</option>
                  <option value="company">Company</option>
                  <option value="admin">Admin</option>
                </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Password</label>
              <input v-model="userForm.password" type="password" required class="admin-input" placeholder="Enter password">
            </div>
            <div class="flex justify-end space-x-4">
              <button type="button" @click="showUserModal = false" class="admin-btn-outline">
                Cancel
              </button>
              <button type="submit" class="admin-btn-primary">
                Create User
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Create Job Modal -->
    <div v-if="showJobModal" class="fixed inset-0 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div class="bg-slate-800/90 backdrop-blur-xl border border-slate-700/50 rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div class="admin-card-header">
          <h3 class="text-xl font-bold text-white">Create New Job</h3>
          <button @click="showJobModal = false" class="text-slate-400 hover:text-emerald-400 transition-colors">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div class="p-6">
          <form @submit.prevent="createJob" class="space-y-6">
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Job Title</label>
              <input v-model="jobForm.title" type="text" required class="admin-input" placeholder="Enter job title">
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Description</label>
              <textarea v-model="jobForm.description" rows="4" required class="admin-input" placeholder="Enter job description"></textarea>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Job Type</label>
                <select v-model="jobForm.jobType" required class="admin-select">
                  <option value="">Select job type</option>
                  <option value="drilling">Drilling</option>
                  <option value="completion">Completion</option>
                  <option value="production">Production</option>
                  <option value="maintenance">Maintenance</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Pay Rate ($)</label>
                <input v-model="jobForm.payRate" type="number" required class="admin-input" placeholder="Enter pay rate">
              </div>
            </div>
            <div class="flex justify-end space-x-4">
              <button type="button" @click="showJobModal = false" class="admin-btn-outline">
                Cancel
              </button>
              <button type="submit" class="admin-btn-primary">
                Create Job
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import AdminLayout from './AdminLayout.vue'

const router = useRouter()

const adminAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('adminToken') || ''
  return token ? { Authorization: `Bearer ${token}` } : {}
}

// Interfaces
interface AdminStats {
  totalUsers: number
  newUsersThisMonth: number
  activeJobs: number
  jobCompletionRate: number
  totalRevenue: number
  revenueGrowth: number
  platformHealth: number
}

interface Activity {
  id: string
  type: string
  description: string
  status: string
  timestamp: string
}

interface SafetyAlert {
  id: string
  title: string
  severity: 'critical' | 'warning' | 'info'
  location: string
  timestamp: string
}

// State
const showUserModal = ref(false)
const showJobModal = ref(false)

// Form data
const userForm = reactive({
  firstName: '',
  lastName: '',
  email: '',
  userType: '',
  password: ''
})

const jobForm = reactive({
  title: '',
  description: '',
  jobType: '',
  payRate: 0
})

// Real data from database/API
const adminStats = ref<AdminStats>({
  totalUsers: 0,
  newUsersThisMonth: 0,
  activeJobs: 0,
  jobCompletionRate: 0,
  totalRevenue: 0,
  revenueGrowth: 0,
  platformHealth: 0
})

const recentActivity = ref<Activity[]>([])
const safetyAlerts = ref<SafetyAlert[]>([])

// Methods
const getActivityIconClass = (type: string) => {
  switch (type) {
    case 'user':
      return 'admin-badge-success'
    case 'job':
      return 'admin-badge-warning'
    case 'payment':
      return 'admin-badge-success'
    case 'safety':
      return 'admin-badge-danger'
    case 'system':
      return 'admin-badge-default'
    default:
      return 'admin-badge-default'
  }
}

const getActivityStatusBadgeClass = (status: string) => {
  switch (status) {
    case 'completed':
      return 'admin-badge-success'
    case 'investigating':
      return 'admin-badge-warning'
    case 'failed':
      return 'admin-badge-danger'
    default:
      return 'admin-badge-default'
  }
}

const refreshData = async () => {
  console.log('Refreshing admin data...')
  try {
    // Fetch real data from API
    await fetchAdminStats()
    await fetchRecentActivity()
    await fetchSafetyAlerts()
  } catch (error) {
    console.error('Error refreshing data:', error)
  }
}

const viewAnalytics = () => {
  router.push('/admin/analytics')
}

const managePlans = () => {
  router.push('/admin/plans')
}

const manageBlog = () => {
  router.push('/admin/blog')
}

const createUser = async () => {
  console.log('Creating user:', userForm)
  
  try {
    // Call API to create user
    const response = await fetch('/api/admin/users', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...adminAuthHeaders(),
      },
      body: JSON.stringify({
        firstName: userForm.firstName,
        lastName: userForm.lastName,
        email: userForm.email,
        userType: userForm.userType,
        password: userForm.password
      })
    })
    
    if (response.ok) {
  showUserModal.value = false
      await refreshData() // Refresh to show new data

  // Reset form
  Object.assign(userForm, {
    firstName: '',
    lastName: '',
    email: '',
    userType: '',
    password: ''
  })
    } else {
      console.error('Failed to create user')
    }
  } catch (error) {
    console.error('Error creating user:', error)
  }
}

const createJob = async () => {
  console.log('Creating job:', jobForm)
  
  try {
    // Call API to create job
    const response = await fetch('/api/admin/jobs', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...adminAuthHeaders(),
      },
      body: JSON.stringify({
        title: jobForm.title,
        description: jobForm.description,
        jobType: jobForm.jobType,
        payRate: jobForm.payRate
      })
    })
    
    if (response.ok) {
  showJobModal.value = false
      await refreshData() // Refresh to show new data

  // Reset form
  Object.assign(jobForm, {
    title: '',
    description: '',
    jobType: '',
    payRate: 0
  })
    } else {
      console.error('Failed to create job')
    }
  } catch (error) {
    console.error('Error creating job:', error)
  }
}

// API Functions
const asList = (data: unknown, key: string): any[] => {
  if (Array.isArray(data)) return data
  if (data && typeof data === 'object' && Array.isArray((data as Record<string, unknown>)[key])) {
    return (data as Record<string, any[]>)[key]
  }
  return []
}

const fetchAdminStats = async () => {
  try {
    const response = await fetch('/api/admin/stats', { headers: adminAuthHeaders() })
    if (response.ok) {
      const data = await response.json()
      adminStats.value = {
        totalUsers: Number(data.totalUsers ?? data.users ?? 0),
        newUsersThisMonth: Number(data.newUsersThisMonth ?? 0),
        activeJobs: Number(data.activeJobs ?? data.jobs ?? 0),
        jobCompletionRate: Number(data.jobCompletionRate ?? 0),
        totalRevenue: Number(data.totalRevenue ?? 0),
        revenueGrowth: Number(data.revenueGrowth ?? 0),
        platformHealth: Number(data.platformHealth ?? 100),
      }
    }
  } catch (error) {
    console.error('Error fetching admin stats:', error)
  }
}

const fetchRecentActivity = async () => {
  try {
    const response = await fetch('/api/admin/activity', { headers: adminAuthHeaders() })
    if (response.ok) {
      const data = await response.json()
      recentActivity.value = asList(data, 'activities')
    }
  } catch (error) {
    console.error('Error fetching recent activity:', error)
  }
}

const deleteActivity = async (activityId: string) => {
  try {
    const response = await fetch(`/api/admin/activity/${activityId}`, {
      method: 'DELETE',
      headers: adminAuthHeaders(),
    })
    if (response.ok) {
      recentActivity.value = recentActivity.value.filter(activity => activity.id !== activityId)
    }
  } catch (error) {
    console.error('Error deleting activity:', error)
  }
}

const fetchSafetyAlerts = async () => {
  try {
    const response = await fetch('/api/admin/safety-alerts', { headers: adminAuthHeaders() })
    if (response.ok) {
      const data = await response.json()
      safetyAlerts.value = asList(data, 'alerts')
    }
  } catch (error) {
    console.error('Error fetching safety alerts:', error)
  }
}

const getSafetyAlertIconClass = (severity: string) => {
  switch (severity) {
    case 'critical':
      return 'bg-red-500/20 border border-red-500/30 text-red-400'
    case 'warning':
      return 'bg-yellow-500/20 border border-yellow-500/30 text-yellow-400'
    case 'info':
      return 'bg-blue-500/20 border border-blue-500/30 text-blue-400'
    default:
      return 'bg-slate-500/20 border border-slate-500/30 text-slate-400'
  }
}

const reviewAlert = async (alertId: string) => {
  console.log('Reviewing alert:', alertId)
  // Navigate to alert details or open modal
}

const deleteAlert = async (alertId: string) => {
  try {
    const response = await fetch(`/api/admin/safety-alerts/${alertId}`, {
      method: 'DELETE',
      headers: adminAuthHeaders(),
    })
    if (response.ok) {
      safetyAlerts.value = safetyAlerts.value.filter(alert => alert.id !== alertId)
    }
  } catch (error) {
    console.error('Error deleting alert:', error)
  }
}

// Lifecycle
onMounted(async () => {
  console.log('Admin Panel loaded - fetching real data')
  await fetchAdminStats()
  await fetchRecentActivity()
  await fetchSafetyAlerts()
})
</script>

<style scoped>
/* Admin Card Styles */
.admin-card {
  @apply bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-3xl shadow-2xl shadow-black/10 hover:border-slate-600/50 transition-all duration-500;
}

.admin-card-header {
  @apply flex items-center justify-between p-6 border-b border-slate-700/50;
}

.admin-card-body {
  @apply p-6;
}

/* Stat Cards */
.admin-stat-card {
  @apply bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-3xl p-6 hover:border-emerald-500/30 transition-all duration-500 hover:transform hover:scale-105 hover:shadow-2xl hover:shadow-emerald-500/10 h-full;
}

/* Action Cards */
.admin-action-card {
  @apply bg-slate-800/30 backdrop-blur-sm border border-slate-700/30 rounded-2xl p-6 hover:border-emerald-500/30 transition-all duration-300 hover:transform hover:scale-105 hover:shadow-xl hover:shadow-emerald-500/5 text-left;
}

/* Activity Items */
.activity-item {
  @apply flex items-center space-x-4 p-4 bg-slate-800/30 rounded-2xl border border-slate-700/30 hover:border-slate-600/50 transition-all duration-300;
}

.activity-icon {
  @apply flex-shrink-0;
}

/* Safety Alert Items */
.safety-alert-item {
  @apply flex items-center space-x-4 p-4 bg-slate-800/30 rounded-2xl border border-slate-700/30 hover:border-slate-600/50 transition-all duration-300;
}

/* System Status Items */
.system-status-item {
  @apply p-4 bg-slate-800/30 rounded-2xl border border-slate-700/30;
}

/* Button Styles */
.admin-btn-primary {
  @apply px-6 py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-xl hover:from-emerald-600 hover:to-cyan-600 transition-all duration-300 shadow-lg shadow-emerald-500/25;
}

.admin-btn-outline {
  @apply px-4 py-2 border border-slate-600 text-slate-300 rounded-xl hover:border-emerald-500 hover:text-emerald-400 transition-all duration-300;
}

/* Form Styles */
.admin-input {
  @apply w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300;
}

.admin-select {
  @apply w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300;
}

/* Badge Styles */
.admin-badge-success {
  @apply px-3 py-1 bg-emerald-500/20 text-emerald-400 text-xs font-semibold rounded-full border border-emerald-500/30;
}

.admin-badge-warning {
  @apply px-3 py-1 bg-yellow-500/20 text-yellow-400 text-xs font-semibold rounded-full border border-yellow-500/30;
}

.admin-badge-danger {
  @apply px-3 py-1 bg-red-500/20 text-red-400 text-xs font-semibold rounded-full border border-red-500/30;
}

.admin-badge-default {
  @apply px-3 py-1 bg-slate-500/20 text-slate-400 text-xs font-semibold rounded-full border border-slate-500/30;
}

.activity-badge {
  @apply px-2 py-1 text-xs font-medium rounded-full;
}
</style>